import { useEffect, useRef, useState } from 'react'
import { gearListRows, reviewPointsToday } from 'lib/community/hub'
import { sentCount } from 'lib/community/model'
import type {
  CommunityHelpfulAnswer,
  CommunityMemberResponse,
  CommunityPoints,
  CommunityProduct,
} from 'lib/community/types'
import type { OwnerMeResponse } from 'lib/dealhunt/types'
import { noteAnchorId } from 'lib/owner-notes/band-copy'
import FeedRow from './FeedRow'
import { GearCandidatesBlock, GearOwnerRow } from './GearOwnerLayer'
import OwnPicker from './OwnPicker'
import { useHelpfulMine } from './useHelpfulMine'
import { useOwnMemberMe } from './useOwnMemberMe'
import base from './community.module.css'
import memberStyles from './Member.module.css'
import styles from './MemberHub.module.css'

/**
 * The member's own head on the Gear tab (design 1b), hydrated from `/me` — never in the
 * shared-cached HTML, hidden by "View as visitor": the identity line ("Your owner reviews appear
 * as @x") with `action` beside it ("Add gear you own"), then `children` (the picker it opens).
 * Pure; `MemberGear` feeds it `own` from `useOwnMemberMe`.
 */
export const GearOwnerLayerView: React.FC<
  React.PropsWithChildren<{ handle: string; own: boolean; action?: React.ReactNode }>
> = ({ handle, own, action = null, children }) => {
  if (!own) {
    return null
  }
  return (
    <>
      <div className={styles.gearHead}>
        <p className={styles.identity}>
          {'Your owner reviews appear as '}
          <b>{`@${handle}`}</b>
          {'. Visitors see live reviews only.'}
        </p>
        {action}
      </div>
      {children}
    </>
  )
}

/** "#note-<id>" deep links (the bell, phase 4): bring the row into view and light it for a
 *  moment — ONCE per hash. The public rows are drawn at once; a note only the member sees renders
 *  after `/me` (`ready`), so a hash whose row is missing on the first look waits for that one
 *  retry, then counts as handled. "View as visitor" on/off flips `ready` again and must not jump
 *  the page back; only a NEW hash (hashchange) is handled again. Other hashes (`#reviews`) are
 *  the browser's. */
function useNoteHash(ready: boolean): string | null {
  const [lit, setLit] = useState<string | null>(null)
  const [hash, setHash] = useState<string | null>(null)
  const handled = useRef<string | null>(null)
  useEffect(() => {
    const read = (): void => setHash(window.location.hash.slice(1))
    read()
    window.addEventListener('hashchange', read)
    return () => window.removeEventListener('hashchange', read)
  }, [])
  useEffect(() => {
    if (!hash || !hash.startsWith(noteAnchorId('')) || handled.current === hash) {
      return
    }
    const row = document.getElementById(hash)
    if (!row && !ready) {
      return // the member's rows are not drawn yet: retry once they are
    }
    handled.current = hash
    if (row) {
      row.scrollIntoView({ block: 'center' })
      setLit(hash)
    }
  }, [hash, ready])
  useEffect(() => {
    if (!lit) {
      return undefined
    }
    const timer = window.setTimeout(() => setLit(null), 2400)
    return () => window.clearTimeout(timer)
  }, [lit])
  return lit
}

interface MemberGearProps {
  member: CommunityMemberResponse
  now: number
  /** The summary's points table (`guide.points`); null without a summary. */
  points?: CommunityPoints | null
  /** The summary's `waiting_products` (public, trimmed by the server half). */
  waiting?: CommunityProduct[]
}

/**
 * The Gear tab, pure: `me` is the member's own `/me` (`useOwnMemberMe` — null for a visitor, in
 * the server render and under "View as visitor"), `clock` the browser's time after mount, `lit`
 * the deep-linked row. Tests render it with a `/me` fixture.
 */
export const MemberGearView: React.VFC<
  MemberGearProps & {
    me: OwnerMeResponse | null
    clock: number | null
    lit: string | null
    helpfulMine?: ReadonlyMap<string, CommunityHelpfulAnswer>
    onChanged?: () => void
  }
> = ({
  member,
  now,
  points = null,
  waiting = [],
  me,
  clock,
  lit,
  helpfulMine,
  onChanged = () => undefined,
}) => {
  const own = me !== null
  const [picking, setPicking] = useState(false)
  const [asking, setAsking] = useState(false)
  const rows = gearListRows(member.reviews, me?.shelf ?? null, member.handle)
  const reviews = sentCount(member.counts.reviews) ?? member.reviews.length
  const standing = me?.community ?? null
  const addPoints = reviewPointsToday(standing, points?.review)

  return (
    <div className={styles.main}>
      <GearOwnerLayerView
        handle={member.handle}
        own={own}
        action={
          <button
            type="button"
            className={styles.addGear}
            aria-expanded={picking}
            onClick={() => setPicking(open => !open)}
          >
            {'Add gear you own'}
            {addPoints !== null && <span className={styles.addGearPts}>{`+${addPoints}`}</span>}
          </button>
        }
      >
        {picking && <OwnPicker id="gear-own-results" autoFocus />}
      </GearOwnerLayerView>
      <section id="reviews" className={memberStyles.block} aria-labelledby="reviews-title">
        <h2 id="reviews-title" className={memberStyles.h2}>
          {'Owner reviews '}
          {reviews > 0 && <span className={memberStyles.h2Count}>{reviews}</span>}
        </h2>
        <div>
          {rows.map(row => {
            const anchor = noteAnchorId(row.item.note.id)
            if (row.own) {
              return (
                <GearOwnerRow
                  key={row.item.note.id}
                  row={{ ...row, own: row.own }}
                  now={now}
                  clock={clock}
                  points={points}
                  highlighted={lit === anchor}
                  onChanged={onChanged}
                />
              )
            }
            return (
              <FeedRow
                key={row.item.note.id}
                item={row.item}
                now={now}
                showAuthor={false}
                helpfulMine={helpfulMine}
                anchorId={anchor}
                highlighted={lit === anchor}
              />
            )
          })}
          {rows.length === 0 && (
            // A member without a review (2026-10-04): the ask, for whoever reads. The member has
            // "Add gear you own" above, so the button is the visitor's only.
            <div className={memberStyles.emptyReviews}>
              <p className={base.text}>
                {
                  'No owner reviews yet. Replies and Helpful votes count too — an owner review on gear you own adds more.'
                }
              </p>
              {!own &&
                (asking ? (
                  <OwnPicker id="member-own-results" autoFocus />
                ) : (
                  <button type="button" className={base.btn} onClick={() => setAsking(true)}>
                    {'Leave an owner review →'}
                  </button>
                ))}
            </div>
          )}
        </div>
      </section>
      {me && (
        <GearCandidatesBlock
          shelf={me.shelf}
          waiting={waiting}
          points={points}
          standing={standing}
        />
      )}
    </div>
  )
}

/**
 * The Gear tab (Member Hub 1b). The server HTML is the visitor page: every live owner review,
 * each review's text once (Directive dcbf4ee5), the same for every reader. For the member
 * (`useOwnMemberMe`, after hydration) the list becomes their shelf (`gearListRows`: one list, each
 * note once with its status, live rows keeping their place) and the owner layer joins: the
 * identity line with "Add gear you own" (the picker, the existing review flow), each row's status
 * and actions (`GearOwnerRow`), and "Own one of these?" (`GearCandidatesBlock`). `points` and
 * `waiting` come from the summary the page already reads; absent = no figure, no fallback tiles.
 */
export const MemberGear: React.VFC<MemberGearProps> = props => {
  const [epoch, setEpoch] = useState(0)
  const me = useOwnMemberMe(props.member.handle, { epoch })
  const helpfulMine = useHelpfulMine(props.member.reviews.map(item => item.note.id))
  // The browser's clock, set after mount — the six-month hint compares with it (never `now`,
  // the server's render time).
  const [clock, setClock] = useState<number | null>(null)
  useEffect(() => setClock(Date.now()), [])
  const lit = useNoteHash(me !== null)
  return (
    <MemberGearView
      {...props}
      me={me}
      clock={clock}
      lit={lit}
      helpfulMine={helpfulMine}
      onChanged={() => setEpoch(n => n + 1)}
    />
  )
}
